import { ANALYTICS_NAVIGATION_MAP, DashboardTiles } from '@common/constants';
import {
  AvailableDays,
  NavigationType,
  NumberSnapshotResponse,
} from '@common/types';
import { DashboardIcon, MenuItem } from '@cloudclevr/components';
import { useDashboardTileSaveMutation } from '@hooks/dashboard';
import {
  useActivityPerDayTrendQuery,
  useCallsMadeSnapshotQuery,
  useEmailsSentSnapshotQuery,
  useMeetingsSentSnapshotQuery,
  useMessagesSentSnapshotQuery,
} from '@hooks/productivity';
import { NumberTile } from '@pages/InsightsAndAnalytics/tiles/NumberTile';
import {
  formatNumberWithSuffix,
  getTrendData,
  getTrendLabels,
} from '@utils/index';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChartTickFormat,
  LineChartTile,
  LineChartTypes,
} from '../tiles/LineChartTile';
import styles from './styles.module.css';
import { useExtendedTimeSeriesQuery, useProductivitySummaryQuery, useSummaryApisEnabled, useSummaryApisSelectionResolved, useFreshnessIndicatorEnabled, pickSummary } from '@hooks/summary';
import { getLatestUpdateTime, formatFreshnessTimestamp } from '@hooks/summary/freshness';
import { toCallsMadeSnapshot, toEmailsSentSnapshot, toMessagesSentSnapshot, toMeetingsSnapshot, toActivityPerDayTrend } from '@hooks/summary/adapters';
import { ResourceSummaryDto } from '@hooks/summary/types';

type DaysSelection = {
  calls: AvailableDays;
  emails: AvailableDays;
  messages: AvailableDays;
  meetings: AvailableDays;
  activity: AvailableDays;
};

const ProductivityPage: React.FC = () => {
  const navigate = useNavigate();

  const [days, setDays] = useState<DaysSelection>({
    emails: '30',
    calls: '30',
    messages: '30',
    meetings: '30',
    activity: '30',
  });
  const [totalActivity, setTotalActivity] = useState('allactivities');

  const dashboardSaveMutation = useDashboardTileSaveMutation();

  // Summary API: single request for UserActivity when feature flag is on.
  const summaryEnabled = useSummaryApisEnabled();
  const selectionResolved = useSummaryApisSelectionResolved();
  const { data: summaryData, isLoading: isSummaryLoading } = useProductivitySummaryQuery();
  const userActivitySummary = pickSummary(summaryData, 'UserActivity');

  const maxSelectedDays = Math.max(
    Number(days.calls),
    Number(days.emails),
    Number(days.messages),
    Number(days.meetings),
    Number(days.activity),
  );

  const extendedDays = maxSelectedDays >= 90
    ? 90
    : maxSelectedDays >= 60
      ? 60
      : undefined;

  const {
    data: extendedTimeSeries,
    isLoading: isExtendedTimeSeriesLoading,
  } = useExtendedTimeSeriesQuery(
    'productivity',
    extendedDays ?? 60,
    summaryEnabled && !!extendedDays,
  );

  const effectiveUserActivitySummary = useMemo<ResourceSummaryDto | undefined>(() => {
    if (!userActivitySummary) return undefined;
    if (!extendedDays || !extendedTimeSeries?.summaries) return userActivitySummary;

    const resourceTypeKey = Object.keys(extendedTimeSeries.summaries).find(
      (key) => key.toLowerCase() === 'useractivity',
    );

    if (!resourceTypeKey) return userActivitySummary;

    return {
      ...userActivitySummary,
      timeSeries: {
        ...userActivitySummary.timeSeries,
        ...extendedTimeSeries.summaries[resourceTypeKey],
      },
    };
  }, [userActivitySummary, extendedDays, extendedTimeSeries]);

  // Legacy hooks disabled when feature flag is on.
  const legacyHooks = {
    calls: useCallsMadeSnapshotQuery({ days: days.calls, enabled: selectionResolved && !summaryEnabled }),
    emails: useEmailsSentSnapshotQuery({ days: days.emails, enabled: selectionResolved && !summaryEnabled }),
    messages: useMessagesSentSnapshotQuery({ days: days.messages, enabled: selectionResolved && !summaryEnabled }),
    meetings: useMeetingsSentSnapshotQuery({ days: days.meetings, enabled: selectionResolved && !summaryEnabled }),
    activity: useActivityPerDayTrendQuery({ activity: totalActivity, days: days.activity, enabled: selectionResolved && !summaryEnabled }),
  };

  const dataHooks = {
    calls: summaryEnabled
      ? { data: toCallsMadeSnapshot(effectiveUserActivitySummary, days.calls), isLoading: !selectionResolved || isSummaryLoading || isExtendedTimeSeriesLoading }
      : legacyHooks.calls,
    emails: summaryEnabled
      ? { data: toEmailsSentSnapshot(effectiveUserActivitySummary, days.emails), isLoading: !selectionResolved || isSummaryLoading || isExtendedTimeSeriesLoading }
      : legacyHooks.emails,
    messages: summaryEnabled
      ? { data: toMessagesSentSnapshot(effectiveUserActivitySummary, days.messages), isLoading: !selectionResolved || isSummaryLoading || isExtendedTimeSeriesLoading }
      : legacyHooks.messages,
    meetings: summaryEnabled
      ? { data: toMeetingsSnapshot(effectiveUserActivitySummary, days.meetings), isLoading: !selectionResolved || isSummaryLoading || isExtendedTimeSeriesLoading }
      : legacyHooks.meetings,
    activity: summaryEnabled
      ? { data: toActivityPerDayTrend(effectiveUserActivitySummary, totalActivity, days.activity), isLoading: !selectionResolved || isSummaryLoading || isExtendedTimeSeriesLoading }
      : legacyHooks.activity,
  };

  const summaryActivityTrendWindow = summaryEnabled
    ? (dataHooks.activity.data?.trend || []).slice(-Number(days.activity))
    : (dataHooks.activity.data?.trend || []);

  const trendData = getTrendData(
    summaryActivityTrendWindow,
    +days['activity']
  );
  const labels = getTrendLabels(
    summaryActivityTrendWindow,
    +days['activity']
  );

  const createMenuItems = (tileType: DashboardTiles): MenuItem[] => [
    {
      id: '0',
      label: 'Add to Dashboard',
      icon: <DashboardIcon classNames="mr-1" />,
      value: 'addToDashboard',
      action: handleItemAction(tileType),
    },
  ];

  const createDropdownMenuItems = (type?: keyof DaysSelection): MenuItem[] => [
    {
      id: '1',
      label: 'Last 7 days',
      value: '7',
      action: () => handleDropdownChange(type, '7'),
    },
    {
      id: '2',
      label: 'Last 30 days',
      value: '30',
      action: () => handleDropdownChange(type, '30'),
    },
    {
      id: '3',
      label: 'Last 60 days',
      value: '60',
      action: () => handleDropdownChange(type, '60'),
    },
    {
      id: '4',
      label: 'Last 90 days',
      value: '90',
      action: () => handleDropdownChange(type, '90'),
    },
  ];

  const activityPerDayMenuItems: MenuItem[] = [
    {
      id: '1',
      label: 'All activities',
      value: 'allactivities',
      action: () => handleTotalActivityDropdown('allactivities'),
    },
    {
      id: '2',
      label: 'Emails',
      value: 'emails',
      action: () => handleTotalActivityDropdown('emails'),
    },
    {
      id: '3',
      label: 'Messages',
      value: 'messages',
      action: () => handleTotalActivityDropdown('messages'),
    },
    {
      id: '4',
      label: 'Calls',
      value: 'calls',
      action: () => handleTotalActivityDropdown('calls'),
    },

    {
      id: '5',
      label: 'Meetings',
      value: 'meetings',
      action: () => handleTotalActivityDropdown('meetings'),
    },
  ];

  const handleTotalActivityDropdown = (value: string) => {
    setTotalActivity(value);
  };

  const handleDropdownChange = (
    type?: keyof DaysSelection,
    value?: AvailableDays
  ) => {
    if (!type) return;
    setDays((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleItemAction = (type: DashboardTiles) => () => {
    const getParametersData = () => {
      switch (type) {
        case DashboardTiles.CallsMade:
          return {
            days: days['calls'],
          };
        case DashboardTiles.EmailsSent:
          return {
            days: days['emails'],
          };
        case DashboardTiles.MessagesSent:
          return {
            days: days['messages'],
          };
        case DashboardTiles.MeetingsSent:
          return {
            days: days['meetings'],
          };
        case DashboardTiles.ActivityPerDay:
          return {
            days: days['activity'],
            activity: totalActivity,
          };
        default:
          return {};
      }
    };

    const parametersData = getParametersData();

    dashboardSaveMutation.mutate({
      parameters: JSON.stringify(parametersData),
      tileType: type,
    });
  };

  const handleButtonClick = (type: NavigationType) => () => {
    navigate(ANALYTICS_NAVIGATION_MAP[type]);
  };

  const itemActivitySelected = activityPerDayMenuItems.find(
    (item) => item.value === totalActivity
  );

  const getNumberTileData = (data: NumberSnapshotResponse | undefined) => {
    if (!data) return [];
    return [formatNumberWithSuffix(data.value), data.percentage || 0];
  };

  const showFreshness = useFreshnessIndicatorEnabled();
  const freshnessTime = formatFreshnessTimestamp(getLatestUpdateTime(summaryData));

  return (
    <>
      {showFreshness && freshnessTime && (
        <p className="mb-2 text-right text-xs text-gray-400">Data as of {freshnessTime}</p>
      )}
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          <LineChartTile
            sortData={activityPerDayMenuItems}
            compareData={createDropdownMenuItems('activity')}
            legendLabels={[]}
            contextData={createMenuItems(DashboardTiles.ActivityPerDay)}
            initialSelectedItem={itemActivitySelected}
            initialCompareSelectedItem={createDropdownMenuItems().find(
              (item) => item.value === days['activity']
            )}
            tooltipLabel={[itemActivitySelected?.label || '']}
            headerTitle={'Activity per day'}
            isLoading={dataHooks.activity.isLoading}
            labels={labels}
            classNames="col-span-full lg:col-span-6 xl:col-span-6"
            data={[trendData]}
            compareLabel=""
            buttonLabel="Activity data"
            dataSetTypes={[LineChartTypes.Dots]}
            dataSetColors={['#00CF6C']}
            linkPath={ANALYTICS_NAVIGATION_MAP['activityData']}
            showYTicks={true}
            showXTicks={true}
            isDotted={true}
            chartHeight={150}
            xTickFormat={LineChartTickFormat.Date}
            showDateRange={true}
          />
        </div>

        <h1 className={styles.header}>Service activity</h1>
        <div className={styles.pageGrid}>
          <NumberTile
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            sortData={createDropdownMenuItems('emails')}
            initialSelectedItem={createDropdownMenuItems().find(
              (item) => item.value === days['emails']
            )}
            percentageLabel=""
            contextData={createMenuItems(DashboardTiles.EmailsSent)}
            headerTitle={'Emails sent'}
            isLoading={dataHooks.emails.isLoading}
            data={getNumberTileData(dataHooks.emails.data)}
            buttonLabel={'Emails data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['activityData']}
          />

          <NumberTile
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            sortData={createDropdownMenuItems('messages')}
            initialSelectedItem={createDropdownMenuItems().find(
              (item) => item.value === days['messages']
            )}
            percentageLabel=""
            contextData={createMenuItems(DashboardTiles.MessagesSent)}
            headerTitle={'Messages sent'}
            isLoading={dataHooks.messages.isLoading}
            data={getNumberTileData(dataHooks.messages.data)}
            buttonLabel={'Messages data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['activityData']}
          />

          <NumberTile
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            sortData={createDropdownMenuItems('calls')}
            initialSelectedItem={createDropdownMenuItems().find(
              (item) => item.value === days['calls']
            )}
            percentageLabel=""
            contextData={createMenuItems(DashboardTiles.CallsMade)}
            headerTitle={'Calls made'}
            isLoading={dataHooks.calls.isLoading}
            data={getNumberTileData(dataHooks.calls.data)}
            buttonLabel={'Calls data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['activityData']}
          />

          <NumberTile
            classNames="col-span-6 md:col-span-3 xl:col-span-2"
            sortData={createDropdownMenuItems('meetings')}
            initialSelectedItem={createDropdownMenuItems().find(
              (item) => item.value === days['meetings']
            )}
            percentageLabel=""
            contextData={createMenuItems(DashboardTiles.MeetingsSent)}
            headerTitle={'Meetings joined'}
            isLoading={dataHooks.meetings.isLoading}
            data={getNumberTileData(dataHooks.meetings.data)}
            buttonLabel={'Meetings data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['activityData']}
          />
        </div>
      </div>
    </>
  );
};

export default ProductivityPage;
